/**
 * @fileoverview Provide shared hierarchical action navigation from a caller-supplied menu model. This component owns transient submenu visibility and positioning; item commands own domain actions. `AppMenu.js` serves contextual popups, while `AppTabView.js` selects workspace content. All use caller-owned business state.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from "./AppIcon";

const barStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: '8px',
  padding: '0 0.5rem',
  gap: '0'
};

const topItemStyle = {
  position: 'relative',
  padding: '0.6rem 0.85rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: 'var(--text-color)',
  fontWeight: 500,
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  whiteSpace: 'nowrap'
};

const submenuStyle = {
  position: 'fixed',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 5000,
  minWidth: '180px',
  padding: '0.25rem 0'
};

const subItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.55rem 1rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: 'var(--text-color)',
  border: 'none',
  background: 'none',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  position: 'relative'
};



function SubMenu({ items, position, onClose }) {
  const [nestedOpen, setNestedOpen] = useState(null);
  const [nestedPos, setNestedPos] = useState({ top: 0, left: 0 });
  const itemRefs = useRef({});

  const openNested =

  (index) => {
    const el = itemRefs.current[index];
    if (el) {
      const rect = el.getBoundingClientRect();
      setNestedPos({ top: rect.top, left: rect.right + 2 });
    }
    setNestedOpen(index);
  };

  if (!items || items.length === 0) return null;

  return createPortal(
    <div style={{ ...submenuStyle, top: position.top, left: position.left }}>
            {items.map((item, i) =>
      <div key={i} ref={(el) => {itemRefs.current[i] = el;}}>
                    <button
          type="button"
          style={subItemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--surface-hover, var(--surface-ground))';
            if (item.items) openNested(i);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onClick={() => {
            if (item.command) {item.command();onClose();} else
            if (item.items) openNested(i);
          }}>

                        {item.icon && <AppIcon icon={item.icon} />}
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {item.items && <AppIcon icon="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />}
                    </button>
                    {nestedOpen === i && item.items &&
        <SubMenu items={item.items} position={nestedPos} onClose={onClose} />}

                </div>
      )}
        </div>,
    document.body
  );
}



function AppMenubar({ model = [], end }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [subPos, setSubPos] = useState({ top: 0, left: 0 });
  const itemRefs = useRef({});

  useEffect(

  () => {
    if (openIndex === null) return;
    const close =

    (e) => {
      // Don't close if clicking inside a submenu portal
      if (e.target.closest('[data-menubar-item]')) return;
      setOpenIndex(null);
    };
    const timer = setTimeout(() => document.addEventListener('click', close), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', close);
    };
  }, [openIndex]);

  const toggleItem =

  (index) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      const el = itemRefs.current[index];
      if (el) {
        const rect = el.getBoundingClientRect();
        setSubPos({ top: rect.bottom + 4, left: rect.left });
      }
      setOpenIndex(index);
    }
  };

  const endContent = typeof end === 'function' ? end() : end;

  return (
    <div style={barStyle}>
            {model.map((item, i) =>
      <div key={i} ref={(el) => {itemRefs.current[i] = el;}} data-menubar-item>
                    <button
          type="button"
          style={{
            ...topItemStyle,
            backgroundColor: openIndex === i ? 'var(--surface-hover, var(--surface-ground))' : 'transparent',
            borderRadius: '6px'
          }}
          onClick={() => {
            if (item.command) item.command();else
            if (item.items) toggleItem(i);
          }}>

                        {item.icon && <AppIcon icon={item.icon} />}
                        <span>{item.label}</span>
                        {item.items && <AppIcon icon="pi pi-angle-down" style={{ fontSize: '0.65rem', marginLeft: '0.2rem' }} />}
                    </button>
                    {openIndex === i && item.items &&
        <SubMenu items={item.items} position={subPos} onClose={() => setOpenIndex(null)} />}

                </div>
      )}
            {endContent &&
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                    {endContent}
                </div>}

        </div>);

}

export default AppMenubar;
